import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, ArrowRight, Building2 } from "lucide-react";

export default function AdminClientSprints() {
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    slug: "",
    client_name: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    business_address: "",
  });
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/client-sprints`);
      setSprints(res.data.sprints || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load sprints");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!form.client_name || !form.slug) {
      toast.error("Client name and slug are required");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/client-sprints`, form);
      toast.success("Sprint created");
      setOpen(false);
      navigate(`/admin/client-sprints/${res.data.slug}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="text-xs text-emerald-400 uppercase tracking-wider font-semibold">Kennardo James · Consulting</div>
            <h1 className="text-2xl sm:text-3xl font-bold">Federal Contract Setup Sprints</h1>
            <p className="text-sm text-zinc-400 mt-1">15-day, 7-filing sprints. Fill out with clients in-person.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-500 text-black font-semibold" data-testid="new-sprint-btn">
                <Plus className="w-4 h-4 mr-2" /> New Sprint
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle>New Client Setup Sprint</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <Field label="Client / Business Name" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} testid="new-client-name" />
                <Field label="URL slug (kebab-case, unique)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/\s+/g, "-") })} placeholder="e.g. 701-tires" testid="new-client-slug" />
                <Field label="Primary Contact Name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="new-contact-name" />
                <Field label="Phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testid="new-contact-phone" />
                <Field label="Email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="new-contact-email" />
                <Field label="Business Address" value={form.business_address} onChange={(v) => setForm({ ...form, business_address: v })} testid="new-business-address" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="border-zinc-700">Cancel</Button>
                <Button onClick={create} disabled={creating} className="bg-emerald-600 hover:bg-emerald-500 text-black" data-testid="create-sprint-submit">
                  {creating ? "Creating…" : "Create Sprint"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-zinc-500 py-12 text-center">Loading…</div>
        ) : sprints.length === 0 ? (
          <Card className="bg-zinc-950 border-zinc-800 p-10 text-center">
            <Building2 className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <div className="text-zinc-400">No sprints yet. Create your first one.</div>
          </Card>
        ) : (
          <div className="grid gap-3">
            {sprints.map((s) => {
              const submitted = Object.values(s.filings || {}).filter(
                (f) => f.status === "submitted" || f.status === "approved"
              ).length;
              const total = Object.keys(s.filings || {}).length;
              return (
                <Card
                  key={s.slug}
                  onClick={() => navigate(`/admin/client-sprints/${s.slug}`)}
                  className="bg-zinc-950 border-zinc-800 p-4 sm:p-5 hover:border-emerald-700 cursor-pointer transition-colors"
                  data-testid={`sprint-row-${s.slug}`}
                >
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-semibold text-white text-lg">{s.client_name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        {s.contact_name || "—"} · /{s.slug}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <Badge className="bg-zinc-900 border border-zinc-700 text-zinc-300 text-xs">
                        {submitted}/{total} submitted
                      </Badge>
                      <ArrowRight className="w-4 h-4 text-zinc-500" />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1 block">{label}</label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-zinc-900 border-zinc-700 text-white"
        data-testid={testid}
      />
    </div>
  );
}
